import type { UIMessage } from "@ai-sdk/react"
import React, { useEffect, useRef } from "react"
import { AnimatePresence } from "framer-motion"
import { VerticalStackStretch } from "@mallardbay/lib-react-components"

import ChatMessage from "~components/shared/chat/chat-message"
import ChatTypingIndicator from "~components/shared/chat/chat-typing-indicator"
import { isMessageFromAi } from "~components/shared/chat/chat-helpers"
import AnimatedMessage, {
    MESSAGE_ALIGN,
} from "~components/shared/motion/animated-message"

const TYPING_INDICATOR_KEY = "typing-indicator"

interface Props {
    readonly messages: UIMessage[]
    readonly isWaitingForAiResponse: boolean
}

export default function ChatMessageList({
    messages,
    isWaitingForAiResponse,
}: Props) {
    const messagesEndRef = useRef<HTMLDivElement>(null)

    useScrollToLatestMessage({
        messagesEndRef,
        messageCount: messages.length,
        isWaitingForAiResponse,
    })

    return (
        <>
            <VerticalStackStretch>
                <AnimatePresence initial={false}>
                    {messages.map((message) => (
                        <AnimatedMessage
                            key={message.id}
                            align={getMessageAlign(message)}
                        >
                            <ChatMessage message={message} />
                        </AnimatedMessage>
                    ))}
                    {isWaitingForAiResponse && (
                        <AnimatedMessage
                            key={TYPING_INDICATOR_KEY}
                            align={MESSAGE_ALIGN.AI}
                        >
                            <ChatTypingIndicator />
                        </AnimatedMessage>
                    )}
                </AnimatePresence>
            </VerticalStackStretch>
            <div ref={messagesEndRef} />
        </>
    )
}

function getMessageAlign(message: UIMessage) {
    return isMessageFromAi(message) ? MESSAGE_ALIGN.AI : MESSAGE_ALIGN.USER
}

// Scroll only when a new message is appended (user turn starts, AI turn
// starts) or the typing indicator toggles. We deliberately do NOT scroll
// as the AI streams tokens into the existing last message — the reader
// should keep control of their scroll position and follow along at their
// own pace, matching how ChatGPT/Claude handle long responses.
function useScrollToLatestMessage({
    messagesEndRef,
    messageCount,
    isWaitingForAiResponse,
}: {
    readonly messagesEndRef: React.RefObject<HTMLDivElement>
    readonly messageCount: number
    readonly isWaitingForAiResponse: boolean
}) {
    useEffect(() => {
        if (!messagesEndRef.current) return

        messagesEndRef.current.scrollIntoView({
            behavior: "smooth",
            block: "end",
        })
    }, [messagesEndRef, messageCount, isWaitingForAiResponse])
}
